import { z } from 'zod'

import type { NewsFilterValues } from '@/features/news/filters'
import { searchTextParamSchema } from '@/schemas/common'
import { newsLangSchema } from '@/schemas/market-news'
import {
  newsScopeSchema,
  newsSectorSchema,
  newsShowSchema,
  newsSortSchema,
  newsSourceSchema,
} from '@/schemas/news'

/**
 * The URL of Market update's two tabs: search text, sort and the Filters dialog's groups, plus
 * News's language. Every field falls back instead of throwing (`.catch`), so a hand-edited or
 * stale value lands on the served view, and a value equal to its default is not written
 * (`stripSearchParams` on each route).
 *
 * No `createFileRoute` here, so a loader and a test may import it.
 */
const filterFields = {
  query: searchTextParamSchema
    .transform((text) => text.trim())
    .pipe(z.string().min(1))
    .optional()
    .catch(undefined),
  sort: newsSortSchema.default('newest').catch('newest'),
  show: newsShowSchema.default('everything').catch('everything'),
  sectors: z.array(newsSectorSchema).default([]).catch([]),
  sources: z.array(newsSourceSchema).default([]).catch([]),
}

export const marketUpdateSearchSchema = z.object({
  scope: newsScopeSchema.default('all').catch('all'),
  ...filterFields,
})

export const MARKET_UPDATE_SEARCH_DEFAULTS: Required<
  Omit<z.infer<typeof marketUpdateSearchSchema>, 'query'>
> = {
  scope: 'all',
  sort: 'newest',
  show: 'everything',
  sectors: [],
  sources: [],
}

export const marketNewsSearchSchema = z.object({
  scope: newsScopeSchema.default('all').catch('all'),
  ...filterFields,
  lang: newsLangSchema.default('en').catch('en'),
})

export const MARKET_NEWS_SEARCH_DEFAULTS: Required<
  Omit<z.infer<typeof marketNewsSearchSchema>, 'query'>
> = {
  scope: 'all',
  sort: 'newest',
  show: 'everything',
  sectors: [],
  sources: [],
  lang: 'en',
}

export const newsArticleSearchSchema = z.object({
  id: searchTextParamSchema.pipe(z.string().min(1)),
  lang: newsLangSchema.default('en').catch('en'),
})

type FilterSearch = Pick<
  z.infer<typeof marketUpdateSearchSchema>,
  'scope' | 'show' | 'sectors' | 'sources'
>

/** The dialog's model of a URL. */
export const filtersFromSearch = (search: FilterSearch): NewsFilterValues => ({
  scope: search.scope,
  show: search.show,
  sectors: search.sectors,
  sources: search.sources,
})

/** The URL's model of the dialog's filters. */
export const searchFromFilters = (filters: NewsFilterValues): FilterSearch => ({
  scope: filters.scope,
  show: filters.show,
  sectors: [...filters.sectors],
  sources: [...filters.sources],
})
